import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';

const UsernameSetup: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const [username, setUsername] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const validateUsername = (value: string): boolean => {
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    return usernameRegex.test(value);
  };

  const checkAvailability = useCallback(async (value: string) => {
    if (!validateUsername(value)) return;

    setIsChecking(true);
    try {
      const available = await authService.isUsernameAvailable(value);
      setIsAvailable(available);
      if (!available) {
        setError('Username is already taken');
      } else {
        setError('');
      }
    } catch (error) {
      setError('Error checking username availability');
      setIsAvailable(null);
    }
    setIsChecking(false);
  }, []);

  // Drive the availability check off `username` state so it runs no matter how
  // the value is set (typing or the "Use suggested" button).
  useEffect(() => {
    setIsAvailable(null);
    setError('');

    if (!validateUsername(username)) {
      return;
    }

    // Debounce the availability check
    const timeoutId = setTimeout(() => checkAvailability(username), 500);
    return () => clearTimeout(timeoutId);
  }, [username, checkAvailability]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    setUsername(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    if (!validateUsername(username)) {
      setError('Username must be 3-20 characters long and contain only letters, numbers, underscores, or hyphens');
      return;
    }
    
    if (isAvailable !== true) {
      setError('Please choose an available username');
      return;
    }

    setIsSubmitting(true);
    try {
      // Reserve username and update user profile
      await authService.reserveUsername(user.uid, username);
      await authService.setUsername(user.uid, username);
      
      // Update local user state
      await updateUserProfile({ username, isFirstTime: false });
    } catch (error: any) {
      setError(error.message || 'Failed to set username');
    }
    setIsSubmitting(false);
  };

  const suggestedUsername = user?.displayName?.toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 15) || '';

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="max-w-md w-full mx-4">
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '48px 32px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          textAlign: 'center'
        }}>
          {/* User Info */}
          <div style={{ marginBottom: '32px' }}>
            {user?.photoURL && (
              <img
                src={user.photoURL}
                alt="Profile"
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  margin: '0 auto 16px',
                  border: '3px solid #e5e7eb'
                }}
              />
            )}
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '8px'
            }}>
              Welcome, {user?.displayName}!
            </h1>
            <p style={{
              color: '#6b7280',
              fontSize: '14px'
            }}>
              Choose a username to complete your setup
            </p>
          </div>

          {/* Username Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Username
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder={suggestedUsername || 'Enter username'}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    paddingRight: '40px',
                    border: `2px solid ${
                      error ? '#ef4444' : 
                      isAvailable === true ? '#10b981' : 
                      isAvailable === false ? '#ef4444' : '#e5e7eb'
                    }`,
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  disabled={isSubmitting}
                />
                {/* Status Icon */}
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}>
                  {isChecking ? (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #e5e7eb',
                      borderTop: '2px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  ) : isAvailable === true ? (
                    <svg width="16" height="16" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : isAvailable === false ? (
                    <svg width="16" height="16" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : null}
                </div>
              </div>
              
              {/* Helper Text */}
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '4px',
                textAlign: 'left'
              }}>
                3-20 characters, letters, numbers, underscores, or hyphens only
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!username || isAvailable !== true || isSubmitting}
              style={{
                width: '100%',
                padding: '16px',
                background: (!username || isAvailable !== true || isSubmitting) 
                  ? '#f3f4f6' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: (!username || isAvailable !== true || isSubmitting) ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (!username || isAvailable !== true || isSubmitting) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Setting up...
                </>
              ) : (
                'Complete Setup'
              )}
            </button>
          </form>

          {/* Suggestion */}
          {suggestedUsername && !username && (
            <button
              onClick={() => setUsername(suggestedUsername)}
              style={{
                marginTop: '16px',
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Use suggested: {suggestedUsername}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UsernameSetup;